import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';

/**
 * MobileTabTooltip — shows tab title tooltips on mobile via long-press.
 * Renders nothing visible. Attaches a long-press listener to the nearest TabsList.
 * On long-press (500ms hold), shows the tab's `title` attribute as a bottom card.
 */
const MobileTabTooltip = () => {
  const [tooltip, setTooltip] = useState(null);
  const timerRef = useRef(null);
  const containerRef = useRef(null);

  const handleTouchStart = useCallback((e) => {
    const tab = e.target.closest('[title]');
    if (!tab || !tab.getAttribute('title')) return;
    timerRef.current = setTimeout(() => {
      e.preventDefault();
      setTooltip(tab.getAttribute('title'));
    }, 500);
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    const parent = containerRef.current?.closest('.overflow-x-auto') || containerRef.current?.parentElement;
    if (!parent) return;
    parent.addEventListener('touchstart', handleTouchStart, { passive: true });
    parent.addEventListener('touchend', handleTouchEnd);
    parent.addEventListener('touchmove', handleTouchEnd);
    return () => {
      parent.removeEventListener('touchstart', handleTouchStart);
      parent.removeEventListener('touchend', handleTouchEnd);
      parent.removeEventListener('touchmove', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchEnd]);

  return (
    <>
      <span ref={containerRef} className="hidden" />
      {tooltip && (
        <>
          <div className="fixed inset-0 z-9999 md:hidden" onClick={() => setTooltip(null)} onTouchEnd={() => setTooltip(null)} />
          <div className="fixed left-4 right-4 bottom-24 z-10000 bg-slate-800 text-white text-sm p-4 rounded-xl shadow-2xl border border-slate-700 md:hidden">
            <button
              onClick={() => setTooltip(null)}
              className="absolute top-2 right-2 text-slate-400 hover:text-white p-1"
            >
              <X className="w-4 h-4" />
            </button>
            <p className="pr-6 leading-relaxed">{tooltip}</p>
          </div>
        </>
      )}
    </>
  );
};

export default MobileTabTooltip;
